import { createColumnHelper, type SortingState } from '@tanstack/react-table';
import { Loader2, Lock, MoreHorizontal, Shield, Trash } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetAllUsers } from '~entities/users/api/get-all-users';
import type { UserWithRole } from '~entities/users/model/types';
import { useCurrentUser } from '~entities/users/model/user-store';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';

import { DeleteUserDialog } from './delete-user-dialog';
import { EditUserRoleDialog } from './edit-user-role-dialog';
import { ResetPasswordDialog } from './reset-password-dialog';

// Define the user type for table display
interface User {
  id?: string;
  name: string;
  email: string;
  role: string;
  // Keep original user data for actions
  originalUser?: UserWithRole;
  isCurrentUser?: boolean;
}

// Create a column helper
const columnHelper = createColumnHelper<User>();

export const UsersTable = () => {
  const { data: usersData, isLoading } = useGetAllUsers();
  const currentUser = useCurrentUser();

  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  // Filter users based on search query
  const filteredUsers = (usersData ?? [])
    .map((user) => {
      const searchLower = searchQuery.toLowerCase();
      const match =
        user.firstName.toLowerCase().includes(searchLower) ||
        user.lastName.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.role.name.toLowerCase().includes(searchLower);

      if (match) {
        const isCurrentUser = currentUser?.id === user.id;
        return {
          ...user,
          name: `${user.firstName} ${user.lastName}${isCurrentUser ? ' (You)' : ''}`,
          role: user.role.name,
          originalUser: user,
          isCurrentUser,
        };
      }
    })
    .filter((user) => user !== undefined);

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Name'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4 font-medium capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('email', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Email'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('role', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Role'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.display({
        id: 'actions',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Actions'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          const user = info.row.original.originalUser;
          const isCurrentUser = info.row.original.isCurrentUser ?? false;
          return (
            <div className='px-4'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={isCurrentUser}>
                  <Button
                    variant='ghost'
                    className='size-8 p-0'
                    disabled={isCurrentUser}
                  >
                    <MoreHorizontal className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onClick={() => {
                      if (user) {
                        setSelectedUser(user);
                        setEditRoleDialogOpen(true);
                      }
                    }}
                    disabled={isCurrentUser}
                  >
                    <Shield className='mr-2 size-4' />
                    Edit User Role
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      if (user) {
                        setSelectedUser(user);
                        setResetPasswordDialogOpen(true);
                      }
                    }}
                    disabled={isCurrentUser}
                  >
                    <Lock className='mr-2 size-4' />
                    Reset Password
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className='text-red-600'
                    onClick={() => {
                      if (user) {
                        setSelectedUser(user);
                        setDeleteUserDialogOpen(true);
                      }
                    }}
                    disabled={isCurrentUser}
                  >
                    <Trash className='mr-2 size-4' />
                    Delete User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      }),
    ],
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>System Users</CardTitle>
        <CardDescription>
          Manage system user accounts and permissions
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='size-10 animate-spin' />
          </div>
        ) : (
          <EnrollioTable
            ariaLabel='System users'
            totalLabel='System users'
            filtersLayout='inline'
            filterHeader={
              <Input
                placeholder='Search users...'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                className='w-full md:w-72'
              />
            }
            columns={columns}
            data={filteredUsers}
            sorting={sorting}
            onSortingChange={setSorting}
            isUpdating={isLoading}
          />
        )}
      </CardContent>

      <EditUserRoleDialog
        open={editRoleDialogOpen}
        onOpenChange={setEditRoleDialogOpen}
        user={selectedUser}
        onClose={() => {
          setEditRoleDialogOpen(false);
          setSelectedUser(null);
        }}
      />

      <DeleteUserDialog
        open={deleteUserDialogOpen}
        onOpenChange={setDeleteUserDialogOpen}
        user={selectedUser}
        onClose={() => {
          setDeleteUserDialogOpen(false);
          setSelectedUser(null);
        }}
      />

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
        user={selectedUser}
        onClose={() => {
          setResetPasswordDialogOpen(false);
          setSelectedUser(null);
        }}
      />
    </Card>
  );
};
